import {Moment} from 'moment';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import * as benchlingUtil from 'BenchlingUtil';
import {CalendarEntry, CalendarPartyOptions, CalendarView} from 'calendar/CalendarConstants';
import * as EventConstants from 'constants/EventConstants';
import * as UserDAO from 'dao/UserDAO';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';

const CalendarActions = {
  fetchFiles(startDate: Moment, endDate: Moment, partyArgs: CalendarPartyOptions = {}): void {
    benchlingUtil.asyncThrottle('CalendarActions.fetchFiles', () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CALENDAR_FILES_FETCH,
      });

      return UserDAO.getCalendarFiles(startDate, endDate, partyArgs)
        .done((files) => {
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_FILES_FETCH_COMPLETED,
            files,
          });
        })
        .fail((error) => {
          notifications.notifyError(error);
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_FILES_FETCH_FAILED,
          });
        });
    });
  },

  fetchRequests(startDate: Moment, endDate: Moment, partyArgs: CalendarPartyOptions = {}): void {
    benchlingUtil.asyncThrottle('CalendarActions.fetchRequests', () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CALENDAR_REQUESTS_FETCH,
      });

      return UserDAO.getCalendarRequests(startDate, endDate, partyArgs)
        .done((requests) => {
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_REQUESTS_FETCH_COMPLETED,
            requests,
          });
        })
        .fail((error) => {
          notifications.notifyError(error);
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_REQUESTS_FETCH_FAILED,
          });
        });
    });
  },

  fetchProcessTasks(startDate: Moment, endDate: Moment, partyArgs: CalendarPartyOptions = {}): void {
    benchlingUtil.asyncThrottle('CalendarActions.fetchRequests', () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CALENDAR_PROCESS_TASKS_FETCH,
      });

      return UserDAO.getCalendarProcessTasks(startDate, endDate, partyArgs)
        .done((processTasks) => {
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_PROCESS_TASKS_FETCH_COMPLETED,
            processTasks,
          });
        })
        .fail((error) => {
          notifications.notifyError(error);
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_PROCESS_TASKS_FETCH_FAILED,
          });
        });
    });
  },

  fetchEntries(startDate: Moment, endDate: Moment, partyArgs: CalendarPartyOptions = {}): void {
    benchlingUtil.asyncThrottle('CalendarActions.fetchEntries', () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CALENDAR_ENTRIES_FETCH,
      });

      return UserDAO.getCalendarEntries(startDate, endDate, partyArgs)
        .done((entries) => {
          Object.keys(entries).forEach((date) => {
            entries[date] = _.unique(entries[date], (e: CalendarEntry) => e.id);
          });

          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_ENTRIES_FETCH_COMPLETED,
            entries,
          });
        })
        .fail((error) => {
          notifications.notifyError(error);
          AppDispatcher.dispatch({
            actionType: EventConstants.CALENDAR_ENTRIES_FETCH_FAILED,
          });
        });
    });
  },

  updateEntryTask(date: Moment, entryId: string, notePartProvenanceId: number, completed: boolean): void {
    EntryActions.invalidate(entryId);
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_UPDATE_ENTRY_TASK,
      date,
      entryId,
      notePartProvenanceId,
      completed,
    });
  },

  deselectCalendarItem(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_ITEM_DESELECTED,
    });
  },

  selectCalendarItem(itemId: string, date: Moment) {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_ITEM_SELECTED,
      itemId,
      date,
    });
  },

  navigateDate(visibleDate: Moment): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_DATE_NAVIGATE,
      visibleDate,
    });
  },

  changeView(view: CalendarView): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_VIEW_CHANGE,
      view,
    });
    // When clicking day from month view, eg, popover is opened due to click but needs to be closed.
    CalendarActions.closeCreatePopover();
  },

  toggleProjectHidden(projectId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_TOGGLE_PROJECT_HIDDEN,
      projectId,
    });
  },

  updateFilteredMembers(memberIds: Array<string>, shouldHide: boolean): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_UPDATE_FILTERED_MEMBERS,
      memberIds,
      shouldHide,
    });
  },

  updateToday(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_UPDATE_TODAY,
    });
  },

  dayMouseDown(date: Moment): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_DAY_MOUSE_DOWN,
      date,
    });
  },

  dayMouseOver(date: Moment): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_DAY_MOUSE_OVER,
      date,
    });
  },

  dayMouseUp(x: number, y: number): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_DAY_MOUSE_UP,
      x,
      y,
    });
  },

  closeCreatePopover(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CALENDAR_CLOSE_CREATE_POPOVER,
    });
  },
};

export default CalendarActions;
